import { useMemo, useRef } from 'react';
import useCollection from '@actions/services/firestore/browser/workflow/version/node/useCollection';
import { EntityGet } from '@actions/services/firestore/browser/types';
import { Node } from '@actions/services/firestore/browser/workflow/version/node/types';

type UseWorkflowTriggerParams = {
    workflowId: string;
    versionId: string | null;
};

type UseWorkflowTriggerResult = {
    trigger: EntityGet<Node> | null;
    isLoading: boolean;
    hasLoaded: boolean;
};

const useWorkflowTrigger = ({
    workflowId,
    versionId,
}: UseWorkflowTriggerParams): UseWorkflowTriggerResult => {
    const { data: nodes, isLoading } = useCollection(workflowId, versionId!, {
        disabled: !workflowId || !versionId,
    });

    const startedLoadingRef = useRef(false);
    if (isLoading) {
        startedLoadingRef.current = true;
    }
    const hasLoaded = startedLoadingRef.current && !isLoading;

    const trigger = useMemo(() => {
        if (!nodes) {
            return null;
        }
        return nodes.find(node => node.category === 'triggers') || null;
    }, [nodes]);

    return {
        trigger: trigger as EntityGet<Node> | null,
        isLoading,
        hasLoaded,
    };
};

export default useWorkflowTrigger;
